import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import PageLayout from "../../components/PageLayout";
import Toast from "../../components/Toast";
import { GET_UNDERPAYMENT_LEAK_URL, getData } from "../../utils/api";
import { canAccessSettlementInsights, getLoginDetails } from "../../utils/storage";
import InsightsHeaderIcon from "../../icons/newicons/insights.svg";
import { InsightsSubNav } from "./InsightsSubNav";

import "./insights.css";
import "./cxo-settlement-dashboard.css";
import "./underpayment-leak.css";

function money(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function moneySigned(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const abs = Math.abs(v).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
  if (Math.abs(v) < 0.5) return "$0";
  return v > 0 ? `+${abs}` : `−${abs}`;
}

function oz(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toFixed(2);
}

function pct(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `${Number(n).toFixed(1)}%`;
}

const UnderpaymentLeakInsights = () => {
  const loginDetails = getLoginDetails();
  const canView = canAccessSettlementInsights(loginDetails);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [toastParams, setToastParams] = useState({ message: "", type: "", id: 0 });

  useEffect(() => {
    if (!canView) window.location.href = "/";
  }, [canView]);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await getData(GET_UNDERPAYMENT_LEAK_URL, {});
      setData(res?.data || res || null);
    } catch (e) {
      setData(null);
      setToastParams({
        type: "ERROR",
        message: e && e.error ? e.error : "Failed to load underpayment leak analysis",
        id: Date.now(),
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) load();
  }, [canView, load]);

  if (!canView) return null;

  const summary = data?.summary || {};
  const under = data?.underpaidPricing || {};
  const ok = data?.okPricing || {};
  const insights = data?.insights || [];
  const topOems = data?.topOems || [];
  const calcProducts = data?.topCalculatedProducts || [];
  const lots = data?.lots || [];

  return (
    <PageLayout icon={InsightsHeaderIcon} heading="Insights" searchPlaceholder="" hideSearchBar>
      <InsightsSubNav />

      <div className="cxo-page leak-page">
        <div className="cxo-hero">
          <div>
            <p className="cxo-hero-kicker">CXO · Underpayment leak</p>
            <h2 className="cxo-hero-title">Why are we underpaid?</h2>
            <p className="cxo-hero-sub">
              Drill from underpaid ceramic lots → Load invoices → LoadItem OEMs / productIds,
              adjustedPrice, and isCalculatedPrice — to find patterns behind negative $ var.
            </p>
          </div>
          <div className="leak-hero-actions">
            <Link to="/insights/cxo-settlement" className="cxo-hero-link">
              CXO Settlement
            </Link>
            <Link to="/insights/lot-hedge" className="cxo-hero-link">
              Lot Settlement
            </Link>
          </div>
        </div>

        {isLoading && !data ? (
          <div className="cxo-loading">Analyzing underpaid lots → invoices → items…</div>
        ) : (
          <>
            <div className="cxo-kpi-grid">
              <div className="cxo-kpi">
                <div className="cxo-kpi-label">Underpaid lots</div>
                <div className="cxo-kpi-value">{summary.underpaidLots ?? 0}</div>
                <div className="cxo-kpi-hint">Partner paid &lt; our ceramic $</div>
              </div>
              <div className="cxo-kpi">
                <div className="cxo-kpi-label">−$ var total</div>
                <div className="cxo-kpi-value is-neg">
                  {moneySigned(summary.totalNegPayVar)}
                </div>
                <div className="cxo-kpi-hint">Sum of negative payment variance</div>
              </div>
              <div className="cxo-kpi">
                <div className="cxo-kpi-label">Cash-only leak</div>
                <div className="cxo-kpi-value">{summary.cashOnlyLots ?? 0}</div>
                <div className="cxo-kpi-hint">Underpaid but oz OK</div>
              </div>
              <div className="cxo-kpi">
                <div className="cxo-kpi-label">Oz + cash leak</div>
                <div className="cxo-kpi-value">{summary.ozAndCashLots ?? 0}</div>
                <div className="cxo-kpi-hint">Also short returnable oz</div>
              </div>
            </div>

            <div className="leak-calc-banner">
              <div className="leak-calc-banner-title">isCalculatedPrice highlight</div>
              <p className="leak-calc-banner-copy">
                When <code>isCalculatedPrice=true</code>, the line uses a <strong>pre-set
                price</strong> — not the price derived from assays × subweight. Suspect: we
                overpay customers on those pre-set lines, then partners settle lower on real
                metal → underpaid lots. Compare pre-set share / avg $ on underpaid vs OK
                lots below.
              </p>
              <div className="leak-calc-compare">
                <div className="leak-calc-col is-risk">
                  <div className="leak-calc-col-label">Underpaid lots</div>
                  <div className="leak-calc-metric">
                    <span>Avg adjustedPrice</span>
                    <strong>{money(under.avgAdjPrice)}</strong>
                  </div>
                  <div className="leak-calc-metric">
                    <span>Pre-set price items</span>
                    <strong>{pct(under.calcTrueItemPct)}</strong>
                  </div>
                  <div className="leak-calc-metric">
                    <span>Pre-set $ spend</span>
                    <strong>{pct(under.spendProxyCalcPct)}</strong>
                  </div>
                  <div className="leak-calc-metric">
                    <span>Avg $ (pre-set / assay-priced)</span>
                    <strong>
                      {money(under.avgAdjPriceCalc)} / {money(under.avgAdjPriceBook)}
                    </strong>
                  </div>
                </div>
                <div className="leak-calc-col">
                  <div className="leak-calc-col-label">OK settled lots</div>
                  <div className="leak-calc-metric">
                    <span>Avg adjustedPrice</span>
                    <strong>{money(ok.avgAdjPrice)}</strong>
                  </div>
                  <div className="leak-calc-metric">
                    <span>Pre-set price items</span>
                    <strong>{pct(ok.calcTrueItemPct)}</strong>
                  </div>
                  <div className="leak-calc-metric">
                    <span>Pre-set $ spend</span>
                    <strong>{pct(ok.spendProxyCalcPct)}</strong>
                  </div>
                  <div className="leak-calc-metric">
                    <span>Avg $ (pre-set / assay-priced)</span>
                    <strong>
                      {money(ok.avgAdjPriceCalc)} / {money(ok.avgAdjPriceBook)}
                    </strong>
                  </div>
                </div>
              </div>
            </div>

            {insights.length > 0 && (
              <div className="cxo-section">
                <h3 className="cxo-section-title">Key findings</h3>
                <div className="cxo-insights">
                  {insights.map((text, idx) => (
                    <div key={`${idx}-${String(text).slice(0, 24)}`} className="cxo-insight">
                      <span className="cxo-insight-mark">{idx + 1}</span>
                      <span>{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="cxo-section">
              <h3 className="cxo-section-title">Top OEMs on underpaid lots (by adjustedPrice × qty)</h3>
              <p className="cxo-section-hint">
                Ceramic LoadItems only. Pre-set % = share of lines with
                isCalculatedPrice=true (fixed price, not assay×subweight).
              </p>
              <div className="cxo-table-wrap">
                <table className="cxo-table">
                  <thead>
                    <tr>
                      <th>OEM</th>
                      <th className="num">Qty</th>
                      <th className="num">Avg adj $</th>
                      <th className="num">Pre-set %</th>
                      <th className="num">$ spend proxy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topOems.map((row) => (
                      <tr
                        key={row.oem}
                        className={
                          Number(row.calculatedPricePct) >= 80 ? "leak-row-calc" : undefined
                        }
                      >
                        <td>
                          <div className="leak-oem-cell">
                            <span>{row.oem || "—"}</span>
                            {Number(row.calculatedPricePct) >= 80 && (
                              <span className="leak-pill">pre-set $</span>
                            )}
                          </div>
                        </td>
                        <td className="num">{oz(row.qty)}</td>
                        <td className="num">{money(row.avgAdjustedPrice)}</td>
                        <td className="num">{pct(row.calculatedPricePct)}</td>
                        <td className="num">{money(row.spendProxy)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="cxo-section">
              <h3 className="cxo-section-title">
                Pre-set price products (isCalculatedPrice=true)
              </h3>
              <p className="cxo-section-hint">
                Highest spend proxy among ceramic items using pre-set price (not
                assay×subweight) on underpaid lots — prime overpay suspects.
              </p>
              <div className="cxo-table-wrap">
                <table className="cxo-table">
                  <thead>
                    <tr>
                      <th>Description / OEM</th>
                      <th>Family</th>
                      <th>Product ID</th>
                      <th className="num">Qty</th>
                      <th className="num">Avg adj $</th>
                      <th className="num">Avg base $</th>
                      <th className="num">$ spend proxy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calcProducts.map((row) => (
                      <tr key={row.productId} className="leak-row-calc">
                        <td>
                          <div className="leak-oem-cell">
                            <strong>{row.description || row.oem || "—"}</strong>
                            <span className="leak-pill">pre-set $</span>
                          </div>
                          <div className="leak-sub">{row.oem}</div>
                        </td>
                        <td>{row.family || "—"}</td>
                        <td className="leak-mono">{row.productId || "—"}</td>
                        <td className="num">{oz(row.qty)}</td>
                        <td className="num">{money(row.avgAdjustedPrice)}</td>
                        <td className="num">{money(row.avgBasePrice)}</td>
                        <td className="num">{money(row.spendProxy)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="cxo-section">
              <h3 className="cxo-section-title">Underpaid lots</h3>
              <div className="cxo-table-wrap">
                <table className="cxo-table">
                  <thead>
                    <tr>
                      <th>Lot</th>
                      <th>Leak type</th>
                      <th className="num">−$ var</th>
                      <th className="num">Recovery</th>
                      <th className="num">Pre-set item %</th>
                      <th className="num">Pre-set $ %</th>
                      <th className="num">Avg adj $</th>
                      <th className="num">Pt/Pd/Rh var</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lots.map((row) => (
                      <tr key={row.ceramicLot}>
                        <td>
                          <strong>{row.ceramicLot}</strong>
                          <div className="leak-sub">{row.invoiceCount} invoices</div>
                        </td>
                        <td>
                          <span
                            className={`leak-type${
                              row.leakType === "CASH_ONLY" ? " is-cash" : " is-oz"
                            }`}
                          >
                            {row.leakType === "CASH_ONLY" ? "Cash only" : "Oz + cash"}
                          </span>
                        </td>
                        <td className="num is-neg">{moneySigned(row.payVar)}</td>
                        <td className="num">{pct(row.recoveryPct)}</td>
                        <td className="num">{pct(row.calcTrueItemPct)}</td>
                        <td className="num">{pct(row.calcSpendPct)}</td>
                        <td className="num">{money(row.avgAdjustedPrice)}</td>
                        <td className="num leak-mono">
                          {oz(row.ptVar)} / {oz(row.pdVar)} / {oz(row.rhVar)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      <Toast
        message={toastParams.message}
        type={toastParams.type}
        id={toastParams.id}
        clearToastMessage={() => setToastParams({ message: "", type: "", id: 0 })}
      />
    </PageLayout>
  );
};

export default UnderpaymentLeakInsights;
